import React, { useState, useRef, useEffect } from 'react';
import { Modal, View, Text, ScrollView, Pressable, TextInput, StyleSheet, ActivityIndicator } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { getCurrentPartnerTerms, acceptPartnerTerms, AcceptTermsPayload, PartnerTermsVersion } from '../../services/partner-terms-service';
import { isSuccessful } from '../../services/api-requests';
import { notificationService } from '../../services/notification-service';

interface Props {
    visible: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

const PartnerTermsModal: React.FC<Props> = ({ visible, onClose, onSuccess }) => {
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [termsData, setTermsData] = useState<PartnerTermsVersion | null>(null);
    const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [legalName, setLegalName] = useState('');
    const [businessEntity, setBusinessEntity] = useState('');
    const [taxId, setTaxId] = useState('');
    
    const iframeRef = useRef<HTMLIFrameElement>(null);

    useEffect(() => {
        if (visible) {
            fetchTerms();
        } else {
            // Reset state when modal closes
            setHasScrolledToBottom(false);
            setAgreedToTerms(false);
            setLegalName('');
            setBusinessEntity('');
            setTaxId('');
        }
    }, [visible]);

    // Set up iframe scroll listener
    useEffect(() => {
        if (!termsData || !iframeRef.current) return;

        const iframe = iframeRef.current;
        
        const handleIframeScroll = () => {
            try {
                const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
                if (!iframeDocument) return;

                const scrollTop = iframeDocument.documentElement.scrollTop || iframeDocument.body.scrollTop;
                const scrollHeight = iframeDocument.documentElement.scrollHeight || iframeDocument.body.scrollHeight;
                const clientHeight = iframeDocument.documentElement.clientHeight || iframeDocument.body.clientHeight;
                
                const paddingToBottom = 20;
                const isCloseToBottom = scrollTop + clientHeight >= scrollHeight - paddingToBottom;
                
                if (isCloseToBottom && !hasScrolledToBottom) {
                    setHasScrolledToBottom(true);
                }
            } catch (error) {
                // Cross-origin access blocked - this is expected, we'll use an alternative approach
                console.log('Iframe scroll detection limited due to same-origin policy');
            }
        };

        // Try to attach scroll listener to iframe content
        const attachScrollListener = () => {
            try {
                const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDocument) {
                    iframeDocument.addEventListener('scroll', handleIframeScroll);
                }
            } catch (error) {
                console.log('Could not attach scroll listener to iframe');
            }
        };

        // Wait for iframe to load
        iframe.addEventListener('load', attachScrollListener);

        return () => {
            iframe.removeEventListener('load', attachScrollListener);
            try {
                const iframeDocument = iframe.contentDocument || iframe.contentWindow?.document;
                if (iframeDocument) {
                    iframeDocument.removeEventListener('scroll', handleIframeScroll);
                }
            } catch (error) {
                // Ignore cleanup errors
            }
        };
    }, [termsData, hasScrolledToBottom]);

    const fetchTerms = async () => {
        try {
            setLoading(true);
            const response = await getCurrentPartnerTerms('SPLIT_PARTNER_TERMS');
            console.log('Terms response:', response);
            if (isSuccessful(response.status) && response.payload) {
                setTermsData(response.payload as PartnerTermsVersion);
            } else {
                console.error('Failed to load terms:', response);
                notificationService.sendNotification('error', 'Unable to load terms');
                onClose();
            }
        } catch (error: any) {
            console.error('Error fetching terms:', error);
            notificationService.sendNotification('error', error?.message || 'Failed to load terms');
            onClose();
        } finally {
            setLoading(false);
        }
    };

    const handleAccept = async () => {
        if (!termsData) return;

        try {
            setSubmitting(true);
            const payload: AcceptTermsPayload = {
                versionId: termsData.versionId,
                agreementType: 'SPLIT_PARTNER_TERMS',
            };

            // Add optional fields if provided
            if (legalName.trim()) payload.legalName = legalName.trim();
            if (businessEntity.trim()) payload.businessEntity = businessEntity.trim();
            if (taxId.trim()) payload.taxId = taxId.trim();

            const response = await acceptPartnerTerms(payload);
            
            if (isSuccessful(response.status)) {
                notificationService.sendNotification('success', 'Partnership terms accepted successfully!');
                onSuccess();
                onClose();
            } else {
                notificationService.sendNotification('error', (response.payload as any)?.message || 'Failed to accept terms');
            }
        } catch (error: any) {
            notificationService.sendNotification('error', error?.message || 'Failed to accept terms');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDownloadPDF = () => {
        // TODO: Implement PDF download
        notificationService.sendNotification('info', 'PDF download will be available soon');
    };

    // Allow skipping scroll requirement after 5 seconds
    useEffect(() => {
        if (visible && !hasScrolledToBottom) {
            const timer = setTimeout(() => {
                setHasScrolledToBottom(true);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [visible, hasScrolledToBottom]);

    const canAccept = hasScrolledToBottom && agreedToTerms && !submitting;

    const htmlContent = termsData ? `
        <!DOCTYPE html>
        <html>
        <head>
            <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=1.0">
            <style>
                body {
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Arial, sans-serif;
                    padding: 16px;
                    margin: 0;
                    background-color: #1A1D23;
                    color: #FFFFFF;
                    line-height: 1.6;
                }
                h1, h2 { color: #39BEB7; }
                p { margin-bottom: 12px; }
                ul { padding-left: 20px; }
                li { margin-bottom: 8px; }
            </style>
        </head>
        <body>
            ${termsData.content}
        </body>
        </html>
    ` : '';

    return (
        <Modal
            visible={visible}
            animationType="slide"
            transparent={true}
            onRequestClose={onClose}
        >
            <View style={styles.modalOverlay}>
                <View style={styles.modalContainer}>
                    {/* Header */}
                    <View style={styles.header}>
                        <Text style={styles.headerTitle}>Split Partner Terms and Conditions</Text>
                        <Pressable onPress={onClose} style={styles.closeButton}>
                            <Ionicons name="close" size={24} color="#FFFFFF" />
                        </Pressable>
                    </View>

                    {loading ? (
                        <View style={styles.loadingContainer}>
                            <ActivityIndicator size="large" color="#39BEB7" />
                            <Text style={styles.loadingText}>Loading terms...</Text>
                        </View>
                    ) : (
                        <ScrollView 
                            style={styles.contentScrollView}
                            showsVerticalScrollIndicator={false}
                        >
                            {/* Terms Metadata */}
                            {termsData && (
                                <View style={styles.termsMetadata}>
                                    <Text style={styles.metadataText}>Version: {termsData.version}</Text>
                                    <Text style={styles.metadataText}>
                                        Effective Date: {new Date(termsData.effectiveDate).toLocaleDateString()}
                                    </Text>
                                </View>
                            )}

                            {/* HTML Content in iframe */}
                            {termsData && (
                                <View style={styles.iframeContainer}>
                                    <iframe
                                        ref={iframeRef}
                                        srcDoc={htmlContent}
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            border: 'none',
                                            backgroundColor: '#1A1D23',
                                        }}
                                        title="Terms and Conditions"
                                    />
                                </View>
                            )}

                            {!hasScrolledToBottom && (
                                <View style={styles.scrollPrompt}>
                                    <Ionicons name="arrow-down" size={20} color="#39BEB7" />
                                    <Text style={styles.scrollPromptText}>Scroll down in the terms above to continue</Text>
                                </View>
                            )}

                            {/* Divider */}
                            <View style={styles.divider} />

                            {/* Optional Business Information */}
                            <View style={styles.optionalSection}>
                                <Text style={styles.optionalTitle}>Optional Information (for business partners):</Text>
                                
                                <TextInput
                                    style={styles.input}
                                    placeholder="Legal Name"
                                    placeholderTextColor="#8D8E90"
                                    value={legalName}
                                    onChangeText={setLegalName}
                                />
                                
                                <TextInput
                                    style={styles.input}
                                    placeholder="Business Entity"
                                    placeholderTextColor="#8D8E90"
                                    value={businessEntity}
                                    onChangeText={setBusinessEntity}
                                />
                                
                                <TextInput
                                    style={styles.input}
                                    placeholder="Tax ID (EIN/SSN)"
                                    placeholderTextColor="#8D8E90"
                                    value={taxId}
                                    onChangeText={setTaxId}
                                />
                            </View>

                            {/* Divider */}
                            <View style={styles.divider} />

                            {/* Agreement Checkbox */}
                            <Pressable
                                style={styles.checkboxContainer}
                                onPress={() => setAgreedToTerms(!agreedToTerms)}
                            >
                                <View style={[styles.checkbox, agreedToTerms && styles.checkboxChecked]}>
                                    {agreedToTerms && <Ionicons name="checkmark" size={16} color="#FFFFFF" />}
                                </View>
                                <Text style={styles.checkboxLabel}>
                                    I have read and agree to the Split Partner Terms
                                </Text>
                            </Pressable>

                            {/* Action Buttons */}
                            <View style={styles.actionButtons}>
                                <Pressable
                                    style={styles.secondaryButton}
                                    onPress={handleDownloadPDF}
                                >
                                    <Ionicons name="download-outline" size={18} color="#39BEB7" />
                                    <Text style={styles.secondaryButtonText}>Download PDF</Text>
                                </Pressable>

                                <Pressable
                                    style={styles.secondaryButton}
                                    onPress={onClose}
                                >
                                    <Text style={styles.secondaryButtonText}>Decline</Text>
                                </Pressable>

                                <Pressable
                                    style={[
                                        styles.primaryButton,
                                        !canAccept && styles.primaryButtonDisabled
                                    ]}
                                    onPress={handleAccept}
                                    disabled={!canAccept}
                                >
                                    {submitting ? (
                                        <ActivityIndicator size="small" color="#FFFFFF" />
                                    ) : (
                                        <Text style={[
                                            styles.primaryButtonText,
                                            !canAccept && styles.primaryButtonTextDisabled
                                        ]}>
                                            Accept & Continue
                                        </Text>
                                    )}
                                </Pressable>
                            </View>

                            {/* Footer Note */}
                            <Text style={styles.footerNote}>
                                By accepting, you agree to these terms and authorize MeanderEV to process revenue splits to your account.
                            </Text>
                        </ScrollView>
                    )}
                </View>
            </View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        justifyContent: 'center',
        alignItems: 'center',
        padding: 20,
    },
    modalContainer: {
        width: '100%',
        maxWidth: 700,
        height: '90%',
        backgroundColor: '#1A1D23',
        borderRadius: 16,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: 20,
        backgroundColor: '#23262B',
        borderBottomWidth: 1,
        borderBottomColor: '#2A2D35',
    },
    headerTitle: {
        fontSize: 18,
        fontWeight: '700',
        color: '#FFFFFF',
        flex: 1,
    },
    closeButton: {
        padding: 4,
    },
    loadingContainer: {
        flex: 1,
        padding: 40,
        alignItems: 'center',
        justifyContent: 'center',
    },
    loadingText: {
        color: '#8D8E90',
        marginTop: 12,
        fontSize: 14,
    },
    contentScrollView: {
        flex: 1,
    },
    termsMetadata: {
        padding: 20,
        paddingBottom: 12,
        borderBottomWidth: 1,
        borderBottomColor: '#2A2D35',
    },
    metadataText: {
        color: '#8D8E90',
        fontSize: 13,
        marginBottom: 4,
    },
    iframeContainer: {
        height: 350,
        backgroundColor: '#1A1D23',
        marginBottom: 8,
    },
    scrollPrompt: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        paddingVertical: 12,
        paddingHorizontal: 20,
        backgroundColor: 'rgba(57, 190, 183, 0.1)',
        marginHorizontal: 20,
        marginBottom: 8,
        borderRadius: 8,
    },
    scrollPromptText: {
        color: '#39BEB7',
        fontSize: 13,
        fontWeight: '600',
    },
    divider: {
        height: 1,
        backgroundColor: '#2A2D35',
    },
    optionalSection: {
        padding: 20,
    },
    optionalTitle: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '600',
        marginBottom: 12,
    },
    input: {
        backgroundColor: '#23262B',
        borderWidth: 1,
        borderColor: '#2A2D35',
        borderRadius: 8,
        padding: 12,
        color: '#FFFFFF',
        fontSize: 14,
        marginBottom: 12,
    },
    checkboxContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        padding: 20,
        gap: 12,
    },
    checkbox: {
        width: 24,
        height: 24,
        borderWidth: 2,
        borderColor: '#2A2D35',
        borderRadius: 4,
        alignItems: 'center',
        justifyContent: 'center',
    },
    checkboxChecked: {
        backgroundColor: '#39BEB7',
        borderColor: '#39BEB7',
    },
    checkboxLabel: {
        color: '#FFFFFF',
        fontSize: 14,
        flex: 1,
    },
    actionButtons: {
        flexDirection: 'row',
        padding: 20,
        gap: 12,
        flexWrap: 'wrap',
        justifyContent: 'center',
    },
    secondaryButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderRadius: 8,
        borderWidth: 1,
        borderColor: '#39BEB7',
        minWidth: 120,
    },
    secondaryButtonText: {
        color: '#39BEB7',
        fontSize: 14,
        fontWeight: '600',
        textAlign: 'center',
    },
    primaryButton: {
        flexGrow: 1,
        flexShrink: 0,
        minWidth: 150,
        backgroundColor: '#39BEB7',
        paddingVertical: 14,
        paddingHorizontal: 20,
        borderRadius: 8,
        alignItems: 'center',
        justifyContent: 'center',
        height: 46, // Fixed height to prevent expansion
    },
    primaryButtonDisabled: {
        backgroundColor: '#2A2D35',
    },
    primaryButtonText: {
        color: '#FFFFFF',
        fontSize: 14,
        fontWeight: '700',
        textAlign: 'center',
    },
    primaryButtonTextDisabled: {
        color: '#8D8E90',
    },
    footerNote: {
        color: '#8D8E90',
        fontSize: 12,
        textAlign: 'center',
        paddingHorizontal: 20,
        paddingBottom: 20,
        lineHeight: 18,
    },
});

export default PartnerTermsModal;

